import { CalculatorFilled, ExclamationCircleFilled } from '@ant-design/icons'
import { Icon } from '@lmc/ui-kit'
import { Button, Heading, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Image } from 'antd'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { capitalize } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TravelRateCardLevel,
  TravelRateCardStatus,
  useGetTravelRateCardSettings
} from 'screens/Rostering/hooks/useTravelRateCardSettings'
import {
  GoogleAPiStatus,
  InternalDistanceError,
  type RotaEventInstance,
  type TeamMember
} from 'screens/Rostering/types'
import styled from 'styled-components'
import googleLogo from '../images/google_on_white_hdpi.png'
import EditTravelDetailsModal from './EditTravelDetailsModal'

type TravelDetailsProps = {
  teamMember: TeamMember
  teamMemberId: string
  revisionDetail: RotaEventInstance
}

const TravelDetails = ({
  teamMember,
  teamMemberId,
  revisionDetail
}: TravelDetailsProps) => {
  const { t } = useTranslation()

  const flags = useFeatureFlags({
    flagNames: ['travel_and_mileage']
  })?.data

  const flagsTravelTimeAndMileageEnabled = flags?.travel_and_mileage?.enabled

  const { data: rateCards } = useGetTravelRateCardSettings()

  const siteRateCard = rateCards?.find(
    (card) => card.levelType === TravelRateCardLevel.SITE
  )

  const [editTravelDetailsModalVisible, setEditTravelDetailsModalVisible] =
    useState(false)

  if (
    !flagsTravelTimeAndMileageEnabled ||
    siteRateCard?.status !== TravelRateCardStatus.ENABLED ||
    revisionDetail.siteShift
  )
    return null

  const { travelInfo } = teamMember

  const address = revisionDetail?.address?.find(
    (item: { id: string }) => item.id === revisionDetail?.addressId
  )

  const clockInOutDetails = revisionDetail?.clockIns?.find(
    (clockIn: any) => clockIn.teamMemberId === teamMemberId
  )

  const clockedOut = clockInOutDetails?.clockedOut

  const success = travelInfo?.status === GoogleAPiStatus.SUCCESS
  const pending = travelInfo?.status === GoogleAPiStatus.QUEUED
  const error =
    travelInfo?.status === GoogleAPiStatus.FAILED || travelInfo?.errorCode

  const successTravelInfo = travelInfo && success && clockedOut
  const pendingTravelInfo = travelInfo && pending
  const errorTravelInfo = travelInfo && error

  const noTravelInfoButNotClockedOut = !clockedOut
  const noTravelInfo =
    !errorTravelInfo &&
    !(travelInfo?.distanceMeters || travelInfo?.systemDistanceMeters) &&
    clockedOut

  const detailsAddedManually = !!travelInfo?.updatedBy

  const isEditable =
    successTravelInfo || errorTravelInfo || noTravelInfo || detailsAddedManually

  const showGoogleLogo =
    !noTravelInfoButNotClockedOut && !detailsAddedManually && !noTravelInfo

  const getErrorMessage = () => {
    if (
      travelInfo?.errorCode === InternalDistanceError.SAME_ORIGIN_DESTINATION
    ) {
      return t('TRAVEL_DETAILS_SAME_ORIGIN_DESTINATION')
    }

    return t('TRAVEL_DETAILS_ERROR')
  }

  return (
    <GrayWrapper>
      <HeadingSection level={4}>
        <TitleAndLogoSection>
          {t('TRAVEL_DETAILS')}
          {showGoogleLogo && (
            <>
              <StyledDivider type="vertical" />
              <LogoSection>
                <GrayText level={2}>{t('CALCULATED_BY')}</GrayText>
                <Image src={googleLogo} alt="Google Logo" width={70} />
              </LogoSection>
            </>
          )}
          {detailsAddedManually && (
            <>
              <StyledDivider type="vertical" />
              <GrayText level={2}>{t('CALCULATED_MANUALLY')}</GrayText>
            </>
          )}
        </TitleAndLogoSection>

        {isEditable && (
          <GrayTextButton
            variant="text"
            onClick={() => setEditTravelDetailsModalVisible(true)}
          >
            <Icon name="EditOutlined" />
            {successTravelInfo ? t('EDIT_DETAILS') : t('MANUALLY_EDIT_DETAILS')}
          </GrayTextButton>
        )}
      </HeadingSection>

      <Divider />

      {(successTravelInfo || detailsAddedManually) && (
        <DetailsSection>
          <InfoSection>
            <GrayText level={2}>{t('TRAVEL_TIME')}</GrayText>
            <Text>{travelInfo.travelTimeText}</Text>
          </InfoSection>

          <InfoSection>
            <GrayText level={2}>{t('TRAVEL_DISTANCE')}</GrayText>
            <Text>{travelInfo.distanceText}</Text>
          </InfoSection>

          <InfoSection>
            <GrayText level={2}>{t('TRAVEL_MODE')}</GrayText>
            <Text>{capitalize(travelInfo.mode)}</Text>
          </InfoSection>
        </DetailsSection>
      )}

      {pendingTravelInfo && (
        <StatusSection>
          <StyledCalculatorFilled />
          <Text>{t('TRAVEL_DETAILS_PENDING')}</Text>
        </StatusSection>
      )}

      {errorTravelInfo && (
        <StatusSection>
          <StyledExclamationCircleFilled />
          <Text>{getErrorMessage()}</Text>
        </StatusSection>
      )}

      {noTravelInfoButNotClockedOut && (
        <Text>{t('TEAM_MEMBER_CLOCK_OUT')}</Text>
      )}

      {noTravelInfo && <Text>{t('SIGNIFICANT_GAP')}</Text>}

      <EditTravelDetailsModal
        editTravelDetailsModalVisible={editTravelDetailsModalVisible}
        handleCancel={() => setEditTravelDetailsModalVisible(false)}
        teamMember={teamMember}
        instanceId={revisionDetail.id}
        endAddress={address}
        manualEdit={!successTravelInfo}
      />
    </GrayWrapper>
  )
}

export default TravelDetails

const HeadingSection = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const TitleAndLogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const GrayTextButton = styled(Button)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const GrayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background-color: ${(props) => props.theme.color.neutral.color10};
  border-radius: 20px;
`

const GrayText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledDivider = styled(Divider)`
  height: 40px;
`
const DetailsSection = styled.div`
  display: flex;
  justify-content: space-between;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 200px;
`
const StatusSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`
const StyledCalculatorFilled = styled(CalculatorFilled)`
  color: ${(props) => props.theme.color.status.warning};
  font-size: 20px;
  margin-top: 2px;
`
const StyledExclamationCircleFilled = styled(ExclamationCircleFilled)`
  color: ${(props) => props.theme.color.status.danger};
  font-size: 20px;
  margin-top: 2px;
`
